// Health, readiness and identity (13 §Endpoints: auth and identity, health and ops).
import { z } from "zod";
import {
  AccountGrantLevel,
  AgencyGrantLevel,
  AgencyUserRole,
  CompanyRelation,
  EntitlementStatus,
  FeatureKey,
  MembershipKind,
} from "../enums";
import { Email, Slug, Uuid } from "../primitives";
import { OrgCalendarConfig } from "../rows";
import { defineRoute } from "./route";

export const Health = z.object({ status: z.literal("ok") }).meta({ id: "Health" });

/** HARDEN: an operations signal's state against its thresholds (`warn` never makes `/ready` 503). */
export const SignalStatus = z.enum(["ok", "warn", "fail"]).meta({ id: "SignalStatus" });

export const Ready = z
  .object({
    status: z.literal("ready"),
    database: z.literal("ok"),
    /** HARDEN: the operations signals (numbers only; infra/README.md "Monitoring"). */
    checks: z.object({
      database: z.object({ status: SignalStatus, latency_ms: z.number().min(0) }),
      /** The oldest outbox row the relay has not taken yet, and how many wait. */
      queue: z.object({
        status: SignalStatus,
        pending: z.int().min(0).nullable(),
        oldest_age_seconds: z.number().min(0).nullable(),
      }),
      /** Consumer records in `failed` (Operator › Consumers). */
      dead_letters: z.object({ status: SignalStatus, count: z.int().min(0).nullable() }),
      /** Free space of the data directory; null when it cannot be read. */
      disk: z.object({
        status: SignalStatus,
        free_bytes: z.number().min(0).nullable(),
        free_percent: z.number().min(0).max(100).nullable(),
      }),
    }),
  })
  .meta({ id: "Ready" });

/** `{[company_id]: {[feature]: status}}`; `core` is always `active` (ADR-0016 §5). */
export const EntitlementMap = z
  .record(Uuid, z.partialRecord(FeatureKey, EntitlementStatus))
  .meta({ id: "EntitlementMap" });

export const Me = z
  .object({
    user: z.object({ id: Uuid, email: Email, name: z.string() }),
    memberships: z.array(
      z.object({
        company_id: Uuid,
        company_slug: Slug,
        company_name: z.string(),
        /** The company's calendar (S1-17 review #7: entry forms use its weeks and days). */
        company_calendar: OrgCalendarConfig,
        membership_id: Uuid,
        kind: MembershipKind,
        roles: z.array(CompanyRelation),
      }),
    ),
    agency: z
      .object({
        agency_id: Uuid,
        role: AgencyUserRole,
        /** DC-03: the agency's `template_author` relation (05 §DE configuration). */
        template_author: z.boolean(),
        /** DC-05: the explicit `verification_override` relation (pilots of unverified versions). */
        verification_override: z.boolean(),
        grants: z.array(
          z.object({
            company_id: Uuid,
            company_slug: Slug,
            company_name: z.string(),
            company_calendar: OrgCalendarConfig,
            level: AgencyGrantLevel,
          }),
        ),
      })
      .nullable(),
    entitlements: EntitlementMap,
    /**
     * HARDEN-mfa: two-factor sign-in. `enrolled`: the account has a verified authenticator.
     * `operator_required`: an agency user, who always needs it. `required_company_ids`: the
     * companies whose setting makes this user (an owner or admin there) need it. A required user
     * who has not enrolled gets 403 `mfa_enrollment_required` from every route but auth and
     * `/v1/me` (operators everywhere; others in those companies).
     */
    mfa: z.object({
      enrolled: z.boolean(),
      operator_required: z.boolean(),
      required_company_ids: z.array(Uuid),
    }),
    /**
     * FOUNDER-OS: the caller's account and its Founder OS company (`kind = portfolio`), when the
     * caller holds an account grant; the shell pins it first and opens the Portfolio mode there.
     */
    portfolio: z
      .object({ account_id: Uuid, company_id: Uuid, level: AccountGrantLevel })
      .nullable()
      .optional(),
    /**
     * PLATFORM-OPERATOR: the platform operator's display name (`cp.platform_setting
     * operator_name`, set at install), used by every UI text that names who runs the platform
     * ("Submit to …"). Null when unset; the web app then says "Operator".
     */
    operator_name: z.string().nullable().optional(),
  })
  .meta({ id: "Me" });
export type Me = z.infer<typeof Me>;

export const systemRoutes = {
  "health.get": defineRoute({
    feature: "core",
    id: "health.get",
    method: "get",
    path: "/health",
    tag: "health",
    summary: "Liveness",
    access: "public",
    response: { status: 200, schema: Health },
    errors: [],
    emits: [],
  }),
  "health.ready": defineRoute({
    feature: "core",
    id: "health.ready",
    method: "get",
    path: "/ready",
    tag: "health",
    summary:
      "Readiness: the database is reachable through the pooler (503 only when it is not), with the queue, dead-letter and disk signals",
    access: "public",
    response: { status: 200, schema: Ready },
    errors: ["not_ready"],
    emits: [],
  }),
  "me.get": defineRoute({
    feature: "core",
    id: "me.get",
    method: "get",
    path: "/v1/me",
    tag: "me",
    summary: "The signed-in user, memberships, agency grants and entitlement map",
    access: "session",
    response: { status: 200, schema: Me },
    errors: [],
    emits: [],
  }),
} as const;

/**
 * Routes served by a library rather than declared here: Better Auth owns `/v1/auth/*` (13
 * §Endpoints). The route-coverage check accepts them without an operation.
 */
export const externalRoutes = [
  { methods: ["GET", "POST"], path: "/v1/auth/*", owner: "Better Auth (S1-04)" },
  // WOS-MCP: the MCP server (JSON-RPC over Streamable HTTP; its tools are listed by `tools/list`,
  // each running a contracted route) and the Work OS compatibility aliases, which keep Work OS's
  // own paths and shapes (docs/migration/workos-compat.md) and run those MCP tools.
  { methods: ["GET", "POST"], path: "/mcp", owner: "MCP server (WOS-MCP)" },
  { methods: ["GET", "POST"], path: "/mcp/*", owner: "MCP server (WOS-MCP)" },
  {
    methods: ["GET", "POST", "PATCH", "PUT", "DELETE"],
    path: "/workos/*",
    owner: "Work OS compatibility aliases (WOS-MCP)",
  },
] as const;
